// Inbox — surfaces Email Triage approvals. CLAUDE.md §9 approval-gate
// implementation in three legs:
//   1. Approve  → status='approved', writeback pushes the ORIGINAL draft
//      to Gmail Drafts.
//   2. Edit     → INSERT edits(before, after, edited_by, trace_id), then
//      UPDATE approvals SET status='edited', draft_payload=<new body>.
//      Writeback (which selects status IN ('approved','edited')) picks up
//      the row with the OWNER-edited body and pushes that to Gmail Drafts.
//   3. Reject   → status='rejected', no writeback. Approval row stays for
//      audit / diff-trail purposes.
//
// Concurrency / double-click: every mutation uses React Query, and the
// buttons disable on `mutation.isPending`. Belt-and-braces: the DB-side
// `.eq('id', approvalId)` + the approvals_validate_transition trigger
// reject re-submissions of the same row (status=pending → status=pending
// is a no-op; status=edited → status=edited is rejected as an invalid
// transition by the trigger).
//
// Layout: two-column on lg+ (list left, detail right) inspired by the
// Lovable "Email Triage Hub" mock — UI/UX reference only per CLAUDE.md
// §18. Deliberately omitted from the mock: "Approve & Send" label + Send
// icon (N1 — we never call gmail.send), "Rewrite" button (W4-Brain, not
// W2), "Train on this edit" (vapor — no online-learning loop wired),
// confidence bars (data not stored on draft_payload; would require schema
// + select change).
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, CheckCircle2, EyeOff, Mail, Paperclip, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import GlassCard from '@/components/shared/GlassCard';
import PulseDot from '@/components/shared/PulseDot';
import { useAuth } from '@/lib/auth';
import { SendReplyButton } from '@/components/inbox/SendReplyButton';
import {
  SentRepliesSection,
  useSentReplies,
  sentReplyCount,
  failedReplyCount,
} from '@/components/inbox/SentReplies';
import { supabase } from '@/lib/supabase';
import { resolveInboxSelection } from '@/utils/approvalHash';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';

// Shape that lands in `emails.attachment_summary` JSONB — populated by the
// Gmail parser per attachment on inbound mail. Mirrors what we save in
// apps/workers/src/parsers/gmail.ts. `attachment_id` is the Gmail-API
// handle the agreement extractor uses to fetch the bytes; we don't render
// it here, just filename + size + mime.
export type AttachmentSummaryEntry = {
  filename?: string | null;
  size?: number | null;
  mime_type?: string | null;
  attachment_id?: string | null;
};

type EmailRef = {
  id: string;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  snippet: string | null;
  occurred_at: string | null;
  has_attachments: boolean | null;
  attachment_summary: AttachmentSummaryEntry[] | null;
  // Which connected inbox received it (migration 0049); null on rows ingested
  // before attribution existed — those show under the "All" tab only.
  account_email: string | null;
  // A soft-deleted source email should drop its draft from the queue too.
  deleted_at: string | null;
};

type DraftPayload = {
  subject?: string;
  body?: string;
  category?: string;
  is_urgent?: boolean;
};

type ApprovalRow = {
  id: string;
  status: string;
  created_at: string;
  target_id: string;
  target_email_id: string | null;
  owner_id: string;
  draft_payload: DraftPayload;
  external_target_id: string | null;
  decision_reason: string | null;
  trace_id: string;
  emails: EmailRef | null;
};

const APPROVAL_SELECT =
  'id, status, created_at, target_id, target_email_id, owner_id, draft_payload, external_target_id, decision_reason, trace_id, emails:target_email_id (id, from_email, from_name, subject, snippet, occurred_at, has_attachments, attachment_summary, account_email, deleted_at)';

async function fetchPending(): Promise<ApprovalRow[]> {
  const { data, error } = await supabase
    .from('approvals')
    .select(APPROVAL_SELECT)
    .eq('agent', 'email_triage')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data as unknown as ApprovalRow[]) ?? [];
}

async function fetchRecentDecided(): Promise<ApprovalRow[]> {
  const { data, error } = await supabase
    .from('approvals')
    .select(APPROVAL_SELECT)
    .eq('agent', 'email_triage')
    .in('status', ['approved', 'edited', 'rejected', 'sent', 'failed'])
    .order('created_at', { ascending: false })
    .limit(10);
  if (error) throw error;
  return (data as unknown as ApprovalRow[]) ?? [];
}

// Urgent emails the classifier flagged (emails.is_urgent) that have NO draft
// in the approval queue. Draftable urgent mail (needs_reply / b2b_lead /
// internal_team) surfaces in the queue above with a red PulseDot; but urgent
// NON-draftable mail (meeting_invite, agreement, personal_order, fyi_only, …)
// never produces an approval row, so without this query it had zero surface
// anywhere — the "flag urgent items" contract (CLAUDE.md §3/§6) was half-wired.
// Surfaced by the 2026-06-18 pre-handover gap audit (R-EMAIL-URGENT-SURFACE).
// Owner-scoped read enforced by RLS (same as the approvals→emails join above).
export type UrgentEmailRow = {
  id: string;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  snippet: string | null;
  // Full plaintext body — shown in the read-only detail pane when a captured /
  // urgent row is clicked (the list itself renders only the snippet). Nullable:
  // some rows carry only a snippet.
  body_plain: string | null;
  occurred_at: string | null;
  category: string | null;
  account_email: string | null;
};

const URGENT_EMAIL_SELECT =
  'id, from_email, from_name, subject, snippet, body_plain, occurred_at, category, account_email';

// Cap on the urgent fetch (parity with the pending queue's 50). When the raw
// fetch hits this, the strip surfaces a "showing the N most recent" note rather
// than silently hiding older urgent mail — a "flag urgent" surface that quietly
// drops items reads as "you've seen everything" when you haven't.
const URGENT_EMAIL_LIMIT = 50;

async function fetchUrgentEmails(): Promise<UrgentEmailRow[]> {
  const { data, error } = await supabase
    .from('emails')
    .select(URGENT_EMAIL_SELECT)
    .eq('direction', 'inbound')
    .eq('is_urgent', true)
    .is('deleted_at', null)
    .order('occurred_at', { ascending: false })
    .limit(URGENT_EMAIL_LIMIT);
  if (error) throw error;
  return (data as unknown as UrgentEmailRow[]) ?? [];
}

// Recently captured, non-urgent inbound mail (fyi / promo / spam / read-and-
// filed). The Email Triage Hub is draft-centric, so a connected inbox whose
// recent mail is all informational would otherwise look empty ("we're not
// displaying any emails"). This read-only section proves capture is working
// and shows how the agent categorized each message — it is NOT a full mail
// client (no bodies, no per-message actions). Urgent mail has its own strip;
// reply-worthy mail becomes a draft above.
const CAPTURED_EMAIL_LIMIT = 30;

async function fetchCapturedEmails(): Promise<UrgentEmailRow[]> {
  const { data, error } = await supabase
    .from('emails')
    .select(URGENT_EMAIL_SELECT)
    .eq('direction', 'inbound')
    .eq('is_urgent', false)
    .is('deleted_at', null)
    .order('occurred_at', { ascending: false })
    .limit(CAPTURED_EMAIL_LIMIT);
  if (error) throw error;
  return (data as unknown as UrgentEmailRow[]) ?? [];
}

// The categories the draft worker generates replies for (mirror of
// DRAFTABLE_CATEGORIES in agents/email/draft-worker.ts). Captured mail excludes
// these: a reply-worthy email belongs in the draft queue (or, briefly, nowhere
// while the draft is being generated), never in the read-only "no reply needed"
// list. This also removes any dependence on the LIMIT-10 decided list for
// correctness — a drafted email can never enter the captured pool in the first
// place, so it can't reappear here once its approval scrolls out of "decided".
const DRAFTABLE_CATEGORIES = new Set(['needs_reply', 'b2b_lead', 'internal_team']);

// Pure (exported for tests). Non-draftable captured emails with NO approval row
// (pending or decided) — i.e. read + categorized, genuinely nothing to approve.
// Urgent mail is excluded at the query (is_urgent=false) and lives in its own
// strip; an unclassified (null category) email is treated as non-draftable.
export function capturedNotDrafted(
  captured: UrgentEmailRow[],
  pending: Array<{ target_email_id: string | null }>,
  decided: Array<{ target_email_id: string | null }>,
): UrgentEmailRow[] {
  const drafted = new Set<string>();
  for (const r of pending) if (r.target_email_id) drafted.add(r.target_email_id);
  for (const r of decided) if (r.target_email_id) drafted.add(r.target_email_id);
  return captured.filter((e) => !DRAFTABLE_CATEGORIES.has(e.category ?? '') && !drafted.has(e.id));
}

// Keep a read-only panel's selection valid as its list changes (hide / refetch
// / tab switch): preserve the current selection if it's still present, else fall
// back to the first email (or null when empty). Pure + exported for tests.
export function nextReadonlySelection(
  curr: string | null,
  emails: Array<{ id: string }>,
): string | null {
  if (emails.length === 0) return null;
  return curr && emails.some((e) => e.id === curr) ? curr : emails[0].id;
}

// Connected inboxes drive the per-inbox tabs ("All" + one per address). Same
// query shape as Connections.tsx's fetchGoogleAccounts.
async function fetchConnectedInboxes(): Promise<string[]> {
  const { data, error } = await supabase
    .from('google_accounts')
    .select('email')
    .eq('status', 'connected')
    .order('connected_at', { ascending: true });
  if (error) throw error;
  // Lowercased: emails.account_email is stamped lowercase by the gmail mapper,
  // but google_accounts.email stores Google's userinfo casing verbatim — a
  // mixed-case Workspace address would otherwise render a permanently-empty tab.
  return ((data ?? []) as Array<{ email: string }>).map((r) => r.email.toLowerCase());
}

// Pure helper (exported for tests). Urgent emails that have NO approval row in
// the queue — neither pending nor recently-decided — so an urgent needs_reply
// email (which shows in the queue with its own red dot, or in "Recently
// decided") isn't double-listed in the "Flagged urgent" strip. What remains is
// urgent mail the triage pipeline isn't otherwise surfacing — mostly
// non-draftable categories (meeting_invite, agreement, fyi_only, …).
export function unsurfacedUrgentEmails(
  urgent: UrgentEmailRow[],
  pending: Array<{ target_email_id: string | null }>,
  decided: Array<{ target_email_id: string | null }>,
): UrgentEmailRow[] {
  const surfaced = new Set<string>();
  for (const r of pending) if (r.target_email_id) surfaced.add(r.target_email_id);
  for (const r of decided) if (r.target_email_id) surfaced.add(r.target_email_id);
  return urgent.filter((e) => !surfaced.has(e.id));
}

// Returns a clean human-readable size: 88853 → "89 KB".
// Uses SI decimal (1000-based) since the value lives next to filename text,
// not as exact storage math — readability over Gmail-parity (Gmail uses
// binary KB with floor and shows "87 KB" for the same input). Either reads
// fine on the chip; staying SI keeps the helper monomorphic across
// future surfaces.
// Exported for unit-test coverage.
export function formatAttachmentSize(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

// Filter out empty / malformed entries so render paths can assume filename.
// Defensive: attachment_summary is JSONB so a stray null inside the array
// (or a {} placeholder from an older parser) shouldn't crash the page.
export function usableAttachments(
  raw: AttachmentSummaryEntry[] | null | undefined,
): AttachmentSummaryEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((a): a is AttachmentSummaryEntry => !!a && typeof a.filename === 'string');
}

// Category → badge tone. The canonical category set is defined by the
// Email Triage classifier (apps/workers/src/agents/email/prompts/categorize.ts:
// EMAIL_CATEGORIES). Keep this in sync — categories the classifier emits but
// this function doesn't recognize fall through to the neutral fallback (no
// throw, just a muted badge).
//
// Urgency is conveyed separately by the orthogonal `is_urgent` flag — we do
// NOT recolor a needs_reply badge red just because it's urgent. The PulseDot
// + urgent pill in the detail header handle that signal.
function categoryTone(category: string | undefined): {
  bg: string;
  text: string;
} {
  switch (category) {
    case 'needs_reply':
    case 'b2b_lead':
    case 'agreement':
      return { bg: 'bg-forest-soft', text: 'text-forest' };
    case 'internal_team':
      return { bg: 'bg-learning/10', text: 'text-learning' };
    case 'personal_order':
      return { bg: 'bg-attention/10', text: 'text-attention' };
    case 'meeting_invite':
      return { bg: 'bg-sage/10', text: 'text-sage' };
    case 'spam':
      return { bg: 'bg-alert/10', text: 'text-alert' };
    case 'fyi_only':
    case 'bulk_or_promo':
    default:
      return { bg: 'bg-secondary', text: 'text-muted-foreground' };
  }
}

function formatCategory(category: string | undefined): string {
  if (!category) return 'uncategorized';
  return category.replace(/_/g, ' ');
}

const Inbox = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const location = useLocation();

  const pendingQ = useQuery({ queryKey: ['approvals', 'pending'], queryFn: fetchPending });
  const decidedQ = useQuery({ queryKey: ['approvals', 'decided'], queryFn: fetchRecentDecided });
  const urgentQ = useQuery({ queryKey: ['emails', 'urgent'], queryFn: fetchUrgentEmails });
  const capturedQ = useQuery({ queryKey: ['emails', 'captured'], queryFn: fetchCapturedEmails });

  // Per-inbox tabs: 'all' = the consolidated view; anything else = one
  // connected address (emails.account_email). Rows ingested before migration
  // 0049 have no attribution and appear under "All" only.
  const [inboxTab, setInboxTab] = useState<string>('all');
  const inboxesQ = useQuery({
    queryKey: ['google_accounts', 'inbox-tabs'],
    queryFn: fetchConnectedInboxes,
    staleTime: 60_000,
  });
  const inboxTabs = inboxesQ.data ?? [];
  const matchesTab = (acct: string | null | undefined): boolean =>
    inboxTab === 'all' || (acct ?? '').toLowerCase() === inboxTab;
  // Drop drafts whose source email was soft-deleted (e.g. the old seed cleanup)
  // — the draft is meaningless without its email and shouldn't clutter the queue.
  const pending = useMemo(
    () =>
      (pendingQ.data ?? []).filter(
        (r) => matchesTab(r.emails?.account_email) && !r.emails?.deleted_at,
      ),
    [pendingQ.data, inboxTab],
  );
  const decided = useMemo(
    () =>
      (decidedQ.data ?? []).filter(
        (r) => matchesTab(r.emails?.account_email) && !r.emails?.deleted_at,
      ),
    [decidedQ.data, inboxTab],
  );
  const urgent = useMemo(
    () => (urgentQ.data ?? []).filter((e) => matchesTab(e.account_email)),
    [urgentQ.data, inboxTab],
  );
  const captured = useMemo(
    () =>
      capturedNotDrafted(
        (capturedQ.data ?? []).filter((e) => matchesTab(e.account_email)),
        pendingQ.data ?? [],
        decidedQ.data ?? [],
      ),
    [capturedQ.data, pendingQ.data, decidedQ.data, inboxTab],
  );

  // Per-row edit-buffer state. Keyed by approval id; presence in the map
  // means the row is in "edit" mode and `bodyDraft` is what the textarea
  // currently holds (uncommitted).
  const [editing, setEditing] = useState<Record<string, string>>({});

  // Detail-pane selection. Null when no row is selected (empty list, or
  // initial load). Auto-tracked against pendingQ.data below.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Prune editing entries when their row drops out of `pending` (someone
  // approved / rejected / failed it from elsewhere). Keeps `editing` from
  // accumulating stale keys over a long session.
  useEffect(() => {
    if (!pendingQ.data) return;
    const liveIds = new Set(pendingQ.data.map((r) => r.id));
    setEditing((prev) => {
      let mutated = false;
      const next: Record<string, string> = {};
      for (const [id, body] of Object.entries(prev)) {
        if (liveIds.has(id)) next[id] = body;
        else mutated = true;
      }
      return mutated ? next : prev;
    });
  }, [pendingQ.data]);

  // Keep selection in sync with the pending list AND the URL hash.
  //   - First load: respect `#approval=<id>` from the Discord deep-link
  //     OR the in-app command palette / bell deep-link; if present and
  //     the id is in pending, select it. Otherwise select the first row.
  //   - Selected row leaves pending (approved/rejected/raced from another
  //     tab): select the first remaining row, or null if list is empty.
  //   - New rows arrive while a selection is intact: leave it alone.
  //   - User navigates to a *new* `#approval=<id>` (palette/bell while
  //     already on /inbox): honor the new hash even if a selection
  //     exists. This is what `location.hash` in the deps array buys us
  //     vs. the original `[pendingQ.data]`-only dep — see R1 reviewer
  //     B1 for the failure mode without it.
  //
  // Functional updater reads current `selectedId` from React's state
  // queue, so we don't need it in the deps array.
  useEffect(() => {
    if (!pendingQ.data) return;
    const ids = pending.map((r) => r.id);
    const hash = location.hash;
    // Functional updater reads current `selectedId` from React's state
    // queue, so we don't need it in the deps array.
    setSelectedId((curr) => resolveInboxSelection(curr, ids, hash));
  }, [pendingQ.data, pending, location.hash]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['approvals', 'pending'] });
    qc.invalidateQueries({ queryKey: ['approvals', 'decided'] });
  };

  const decide = useMutation({
    mutationFn: async (args: { id: string; status: 'approved' | 'rejected' }) => {
      if (!user) throw new Error('No user');
      const { error } = await supabase
        .from('approvals')
        .update({
          status: args.status,
          decided_by: user.id,
          decided_at: new Date().toISOString(),
          decision_reason:
            args.status === 'approved' ? 'approved from web inbox' : 'rejected from web inbox',
        })
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  // Save = (optional) INSERT edits trail + UPDATE approvals.
  //
  // Two branches keyed off `bodyChanged`:
  //   1. bodyChanged === true  → INSERT edits FIRST, then UPDATE approvals
  //      to status='edited' carrying the new draft_payload.
  //   2. bodyChanged === false → skip the edits INSERT (no diff to record)
  //      and UPDATE approvals to status='approved' directly. Functionally
  //      identical to clicking Approve without entering edit mode.
  //
  // When branch (1) runs, the order matters:
  //   - If INSERT edits fails (RLS / network), the user sees an error and
  //     retries; nothing in the DB changed.
  //   - If INSERT succeeds and UPDATE fails, we have an orphan edits row
  //     and the approval is still pending — the user retries, and the
  //     retry produces a second edits row + a successful UPDATE. The
  //     extra edits row is harmless (audit-only) and visible in the
  //     trail.
  // The reverse order would let writeback fire on the new body before
  // the edits trail exists — bad for audit consistency. So INSERT first.
  //
  // Sacred §9 invariant: `edits` is the diff trail. The trigger on
  // approvals raises `decided_by + decided_at required` for any
  // transition into approved/edited/rejected, which we satisfy here.
  const saveEdit = useMutation({
    mutationFn: async (args: { row: ApprovalRow; newBody: string }) => {
      if (!user) throw new Error('No user');
      // beforePayload is captured at mutate-time from the loaded row, not
      // re-read here. Safe because the `approvals_one_pending_per_target`
      // UNIQUE partial index (migration 0012) prevents a concurrent agent
      // run from overwriting `draft_payload` on a still-pending row — only
      // one pending approval per target can exist, so this row's payload
      // can only be mutated by THIS user action.
      const beforePayload = args.row.draft_payload;
      const afterPayload: DraftPayload = { ...beforePayload, body: args.newBody };

      // Defensive: if the body didn't actually change, skip the edits
      // insert and just approve. No diff = no audit row.
      const bodyChanged = (beforePayload.body ?? '') !== args.newBody;

      if (bodyChanged) {
        const { error: editsErr } = await supabase.from('edits').insert({
          approval_id: args.row.id,
          owner_id: args.row.owner_id,
          edited_by: user.id,
          before_payload: beforePayload,
          after_payload: afterPayload,
          diff_summary: 'body edited via web inbox',
          trace_id: args.row.trace_id,
        });
        if (editsErr) throw editsErr;
      }

      // Deliberately omit `owner_id` from the UPDATE payload. The
      // approvals_owner_update RLS policy's WITH CHECK re-evaluates
      // against NEW; since owner_id is unchanged, it still equals
      // auth.uid() and the check passes. Adding `owner_id: args.row.owner_id`
      // would be redundant at best and a footgun if a future contributor
      // tries to "update" it to a different value.
      const { error: updErr } = await supabase
        .from('approvals')
        .update({
          status: bodyChanged ? 'edited' : 'approved',
          decided_by: user.id,
          decided_at: new Date().toISOString(),
          decision_reason: bodyChanged
            ? 'edited from web inbox'
            : 'approved from web inbox (unchanged)',
          draft_payload: bodyChanged ? afterPayload : beforePayload,
        })
        .eq('id', args.row.id);
      if (updErr) throw updErr;
    },
    onSuccess: (_data, vars) => {
      // Drop the edit-buffer for that row.
      setEditing((prev) => {
        const next = { ...prev };
        delete next[vars.row.id];
        return next;
      });
      invalidateAll();
    },
  });

  // Hide (soft-delete) a captured/urgent email. Sets emails.deleted_at so the
  // row drops from both read-only strips immediately AND is never re-drafted or
  // re-surfaced (the draft + classify workers filter deleted_at IS NULL).
  // Reversible (audit-safe soft delete, §8) — a hidden email can be restored by
  // clearing deleted_at. Owner-scoped: RLS + a belt-and-suspenders owner_id eq.
  const hideEmail = useMutation({
    mutationFn: async (emailId: string) => {
      if (!user) throw new Error('No user');
      const { error } = await supabase
        .from('emails')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', emailId)
        .eq('owner_id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['emails', 'captured'] });
      qc.invalidateQueries({ queryKey: ['emails', 'urgent'] });
    },
    onError: () => {
      // Surfaced inline on the row; nothing else to do — the email stays visible.
    },
  });
  const hidingId = hideEmail.isPending ? (hideEmail.variables as string) : null;

  // Combined pending-state for ANY mutation affecting a given row. Buttons
  // gate on this to prevent click-storms across approve/edit/reject paths.
  const rowPending = (rowId: string): boolean => {
    if (decide.isPending && decide.variables?.id === rowId) return true;
    if (saveEdit.isPending && saveEdit.variables?.row.id === rowId) return true;
    return false;
  };

  const pendingCount = pending.length;
  const selectedRow = useMemo(
    () => pending.find((r) => r.id === selectedId) ?? null,
    [pending, selectedId],
  );

  // Urgent emails with no draft in the queue (read-only strip). See
  // unsurfacedUrgentEmails — excludes anything already represented by a
  // pending or recently-decided approval so the queue's own red-dot rows
  // aren't double-listed. Read-only: these are FYIs, invites, agreements, etc.
  const urgentUnsurfaced = useMemo(
    () => unsurfacedUrgentEmails(urgent, pending, decided),
    [urgent, pending, decided],
  );

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground">
            Email Triage Hub
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            {pendingCount === 0
              ? 'No drafts waiting. New emails surface here within ~60s of arrival.'
              : `${pendingCount} draft${pendingCount === 1 ? '' : 's'} waiting for your approval. Approve writes to Gmail Drafts — you still send manually (N1).`}
          </p>
        </div>
      </motion.header>

      {/* Per-inbox tabs — "All" consolidates every account; one tab per
          connected address filters the whole page (urgent strip + queue +
          recently decided) to mail that inbox received. */}
      {inboxTabs.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Inboxes">
          {['all', ...inboxTabs].map((tab) => {
            const active = inboxTab === tab;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setInboxTab(tab)}
                className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                  active
                    ? 'bg-forest text-primary-foreground shadow-sm'
                    : 'neo-chip text-muted-foreground hover:text-foreground'
                } ${tab === 'all' ? 'uppercase tracking-wide' : 'font-mono'}`}
              >
                {tab === 'all' ? 'All inboxes' : tab}
              </button>
            );
          })}
        </div>
      )}

      {urgentUnsurfaced.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-2"
          aria-label="Urgent emails flagged by triage"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <PulseDot color="red" />
            <h2 className="text-lg font-medium tracking-tight">Flagged urgent</h2>
            <span className="text-xs text-muted-foreground">
              {urgentUnsurfaced.length} email{urgentUnsurfaced.length === 1 ? '' : 's'} the triage
              agent flagged urgent with no reply drafted — read-only, nothing to approve here.
            </span>
          </div>
          <ReadOnlyEmailPanel emails={urgentUnsurfaced} variant="urgent" ownerId={user?.id} />
          {(urgentQ.data?.length ?? 0) >= URGENT_EMAIL_LIMIT && (
            <p className="text-[11px] text-muted-foreground/80">
              Showing the {URGENT_EMAIL_LIMIT} most recent urgent emails — older urgent mail may not
              be listed here.
            </p>
          )}
        </motion.section>
      )}

      <section>
        {pendingQ.isLoading && <p className="text-sm text-muted-foreground">Loading approvals…</p>}
        {pendingQ.isError && (
          <GlassCard hover={false} className="neo-alert">
            <p className="text-sm text-alert">
              Failed to load approvals: {(pendingQ.error as Error).message}
            </p>
          </GlassCard>
        )}

        {pendingQ.data && pending.length === 0 && !pendingQ.isLoading && (
          <GlassCard variant="inset" hover={false}>
            <p className="text-sm text-muted-foreground">
              {captured.length > 0
                ? 'No drafts to approve right now — the recent mail below is informational (read + filed, no reply needed).'
                : 'No drafts pending approval. New reply-worthy mail surfaces here within ~60s of arrival.'}
            </p>
            <p className="mt-2 text-[11px] text-muted-foreground/80">
              When a draft lands, approving writes it to Gmail Drafts — you still send manually
              (N1).
            </p>
          </GlassCard>
        )}

        {pendingQ.data && pending.length > 0 && (
          // Symmetric heights on both columns let the list scroll within
          // its lane while the detail pane stays anchored alongside it.
          // Tracks viewport so the layout breathes on tall screens / short
          // drafts without cropping the action footer.
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:h-[calc(100vh-14rem)] lg:min-h-[560px]">
            {/* List column */}
            <div className="lg:col-span-2 space-y-3 lg:overflow-y-auto lg:pr-2">
              <AnimatePresence mode="popLayout" initial={false}>
                {pending.map((row) => (
                  <motion.div
                    key={row.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <PendingListItem
                      row={row}
                      selected={row.id === selectedId}
                      pending={rowPending(row.id)}
                      onSelect={() => setSelectedId(row.id)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Detail column */}
            <div className="lg:col-span-3 lg:h-full lg:overflow-y-auto">
              {selectedRow ? (
                <DetailPane
                  row={selectedRow}
                  editingBody={editing[selectedRow.id]}
                  onStartEdit={() =>
                    setEditing((prev) => ({
                      ...prev,
                      [selectedRow.id]: selectedRow.draft_payload.body ?? '',
                    }))
                  }
                  onCancelEdit={() =>
                    setEditing((prev) => {
                      const next = { ...prev };
                      delete next[selectedRow.id];
                      return next;
                    })
                  }
                  onChangeBody={(value) =>
                    setEditing((prev) => ({ ...prev, [selectedRow.id]: value }))
                  }
                  onSaveEdit={(newBody) => saveEdit.mutate({ row: selectedRow, newBody })}
                  onApprove={() => decide.mutate({ id: selectedRow.id, status: 'approved' })}
                  onReject={() => decide.mutate({ id: selectedRow.id, status: 'rejected' })}
                  pending={rowPending(selectedRow.id)}
                  saveError={
                    saveEdit.error && saveEdit.variables?.row.id === selectedRow.id
                      ? (saveEdit.error as Error).message
                      : null
                  }
                />
              ) : (
                <GlassCard variant="inset" hover={false} className="h-full min-h-[400px]">
                  <p className="text-sm text-muted-foreground">Select a draft to review.</p>
                </GlassCard>
              )}
            </div>
          </div>
        )}
      </section>

      {captured.length > 0 && (
        <section className="space-y-2" aria-label="Recently captured mail">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-medium tracking-tight">Recently captured</h2>
            <span className="text-xs text-muted-foreground">
              {captured.length} email{captured.length === 1 ? '' : 's'} read + categorized
              {inboxTab !== 'all' ? ` from ${inboxTab}` : ''} — no reply needed, nothing to approve.
            </span>
          </div>
          <ReadOnlyEmailPanel
            emails={captured}
            variant="captured"
            onHide={(id) => hideEmail.mutate(id)}
            hidingId={hidingId}
            hideError={hideEmail.isError ? 'Could not hide — try again.' : null}
            ownerId={user?.id}
          />
          {(capturedQ.data?.length ?? 0) >= CAPTURED_EMAIL_LIMIT && (
            <p className="text-[11px] text-muted-foreground/80">
              Showing the {CAPTURED_EMAIL_LIMIT} most recent captured emails — older mail may not be
              listed here.
            </p>
          )}
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-medium tracking-tight">Recently decided</h2>
        {decidedQ.data && decided.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing decided yet.</p>
        )}
        <ul className="space-y-2">
          {decided.map((row) => (
            <DecidedItem key={row.id} row={row} />
          ))}
        </ul>
      </section>
    </div>
  );
};

type PendingListItemProps = {
  row: ApprovalRow;
  selected: boolean;
  pending: boolean;
  onSelect: () => void;
};

function PendingListItem({ row, selected, pending, onSelect }: PendingListItemProps) {
  const tone = categoryTone(row.draft_payload.category);
  const attachments = usableAttachments(row.emails?.attachment_summary);
  // Selected state: 2px forest ring + soft shadow, plus a left-edge accent
  // bar. Two cues (ring + bar) so users with reduced color discrimination
  // still see the active row.
  return (
    <GlassCard
      hover={false}
      ariaPressed={selected}
      className={`cursor-pointer transition-all relative ${
        selected
          ? 'neo-selected before:absolute before:left-1 before:top-3 before:bottom-3 before:w-1 before:rounded-r before:bg-forest'
          : ''
      } ${pending ? 'opacity-70' : ''}`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground truncate">
          {row.emails?.from_name ?? row.emails?.from_email ?? 'unknown sender'}
        </span>
        {row.draft_payload.is_urgent && <PulseDot color="red" />}
        <span
          className={`neo-chip ml-auto text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text} shrink-0`}
        >
          {formatCategory(row.draft_payload.category)}
        </span>
      </div>
      <p className="text-sm text-foreground font-medium line-clamp-1">
        {decodeHtmlEntities(row.draft_payload.subject ?? row.emails?.subject) || '(no subject)'}
      </p>
      {row.emails?.snippet && (
        <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
          {decodeHtmlEntities(row.emails.snippet)}
        </p>
      )}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {row.emails?.occurred_at && (
          <p className="text-[11px] font-mono text-muted-foreground">
            {formatDistanceToNow(new Date(row.emails.occurred_at), { addSuffix: true })}
          </p>
        )}
        {attachments.length > 0 && (
          <span
            className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"
            aria-label={`${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}
            title={attachments
              .map((a) => {
                const size = formatAttachmentSize(a.size);
                return size ? `${a.filename} · ${size}` : (a.filename ?? '');
              })
              .join('\n')}
          >
            <Paperclip className="w-3 h-3" aria-hidden />
            <span className="font-mono">{attachments.length}</span>
          </span>
        )}
      </div>
    </GlassCard>
  );
}

type DetailPaneProps = {
  row: ApprovalRow;
  editingBody: string | undefined;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeBody: (value: string) => void;
  onSaveEdit: (newBody: string) => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
  saveError: string | null;
};

function DetailPane({
  row,
  editingBody,
  onStartEdit,
  onCancelEdit,
  onChangeBody,
  onSaveEdit,
  onApprove,
  onReject,
  pending,
  saveError,
}: DetailPaneProps) {
  const isEditing = editingBody !== undefined;
  const trimmedNew = isEditing ? editingBody : '';
  const bodyChanged = isEditing && trimmedNew !== (row.draft_payload.body ?? '');
  const saveLabel = !isEditing
    ? 'Approve → Gmail Drafts'
    : bodyChanged
      ? 'Save edits → Gmail Drafts'
      : 'Approve unchanged';

  const tone = categoryTone(row.draft_payload.category);
  // Attachments on the inbound email (NOT on the draft — the draft we
  // write to Gmail Drafts has no attachments today, per N1 / current
  // drafts-writeback contract). Surfacing them here lets Joel remember
  // to manually attach the same file in Gmail before he hits send.
  const originalAttachments = usableAttachments(row.emails?.attachment_summary);

  return (
    <GlassCard hover={false} className="flex flex-col">
      {/* Header */}
      <div className="pb-4 border-b border-border">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span
            className={`neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}
          >
            {formatCategory(row.draft_payload.category)}
          </span>
          {row.draft_payload.is_urgent && (
            <span className="neo-chip flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-alert/10 text-alert">
              <PulseDot color="red" /> urgent
            </span>
          )}
          {row.emails?.occurred_at && (
            <span className="ml-auto text-[11px] font-mono text-muted-foreground">
              {formatDistanceToNow(new Date(row.emails.occurred_at), { addSuffix: true })}
            </span>
          )}
        </div>
        <h3 className="text-lg font-medium text-foreground">
          {decodeHtmlEntities(row.draft_payload.subject ?? row.emails?.subject) || '(no subject)'}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5 truncate">
          From: {row.emails?.from_name ?? 'unknown'}
          {row.emails?.from_email && ` <${row.emails.from_email}>`}
        </p>
      </div>

      {/* Original snippet + attachments. We render the section even if
          snippet is empty, as long as attachments exist — they're often
          the meat of the email (e.g. a contract PDF where the body is
          just "see attached"). */}
      {(row.emails?.snippet || originalAttachments.length > 0) && (
        <div className="py-4 border-b border-border">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Original</p>
          {row.emails?.snippet && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {decodeHtmlEntities(row.emails.snippet)}
            </p>
          )}
          {originalAttachments.length > 0 && (
            <ul
              className="mt-2 flex flex-col gap-1.5"
              aria-label="Attachments on the original email"
            >
              {originalAttachments.map((a, i) => (
                <li
                  key={`${a.attachment_id ?? a.filename ?? 'att'}-${i}`}
                  className="inline-flex items-center gap-2 text-xs"
                >
                  <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden />
                  <span className="text-foreground truncate">{a.filename}</span>
                  {a.size != null && (
                    <span className="font-mono text-muted-foreground shrink-0">
                      · {formatAttachmentSize(a.size)}
                    </span>
                  )}
                  {a.mime_type && (
                    <span className="text-muted-foreground/70 truncate">· {a.mime_type}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Draft body — render inline (not as a nested card) so the GlassCard
          reads as a single panel. The previous `bg-secondary rounded-lg p-4`
          created a visible card-in-card that made the action buttons below
          look detached. Left-border + slight left padding keeps the
          quoted-content affordance without competing with the outer card. */}
      <div className="py-4 flex-1">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
          Drafted reply
        </p>
        {/* Manual-attach reminder. Today our drafts-writeback worker
            writes a TEXT-ONLY draft to Gmail Drafts — no attachment
            programmatically forwarded. If the inbound email had
            attachments and Joel's reply might want to reference them,
            this hint reminds him to attach manually in Gmail before
            sending. Keeps N1 intact (we never touch Gmail send) and
            closes a real workflow gap surfaced 2026-05-27. */}
        {originalAttachments.length > 0 && (
          <div
            className="neo-inset mb-3 flex items-start gap-2 px-3 py-2 text-xs text-attention"
            role="note"
          >
            <Paperclip className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden />
            <span>
              The original email had {originalAttachments.length} attachment
              {originalAttachments.length === 1 ? '' : 's'}. The draft is text-only — if you want to
              forward {originalAttachments.length === 1 ? 'it' : 'any'}, attach manually in Gmail
              before sending.
            </span>
          </div>
        )}
        {isEditing ? (
          <Textarea
            value={editingBody}
            onChange={(e) => onChangeBody(e.target.value)}
            disabled={pending}
            rows={12}
            className="font-sans text-sm"
            aria-label="Edit reply body"
          />
        ) : (
          <pre className="neo-inset p-3 whitespace-pre-wrap break-words font-sans text-sm text-foreground leading-relaxed">
            {row.draft_payload.body ?? '(empty draft)'}
          </pre>
        )}
      </div>

      {saveError && <p className="text-sm text-alert mb-3">Save failed: {saveError}</p>}

      {/* Actions row sits inside the outer GlassCard, not as a detached
          footer. Border-t makes the boundary explicit so the buttons read
          as part of the same panel as the draft body above. */}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-3 mt-2 border-t border-border">
        {isEditing ? (
          <>
            <Button variant="ghost" size="sm" onClick={onCancelEdit} disabled={pending}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => onSaveEdit(trimmedNew)} disabled={pending}>
              <Check className="w-4 h-4 mr-1.5" />
              {pending ? 'Saving…' : saveLabel}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={onReject} disabled={pending}>
              <X className="w-4 h-4 mr-1.5" />
              {pending ? '…' : 'Reject'}
            </Button>
            <Button variant="outline" size="sm" onClick={onStartEdit} disabled={pending}>
              <Pencil className="w-4 h-4 mr-1.5" />
              Edit
            </Button>
            <Button size="sm" onClick={onApprove} disabled={pending}>
              <Check className="w-4 h-4 mr-1.5" />
              {pending ? 'Approving…' : 'Approve → Gmail Drafts'}
            </Button>
          </>
        )}
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground/80 self-start">
        We never call Gmail's send endpoint. Approval writes to Gmail Drafts; you send from Gmail.
      </p>
    </GlassCard>
  );
}

// Two-column list+detail for the READ-ONLY strips (Flagged urgent / Recently
// captured). Mirrors the pending-draft queue's list+detail so clicking an email
// opens it in a side pane (fixing the "clicking does nothing" gap) — but these
// emails carry no draft, so the detail is a read-only preview. `variant` tunes
// the accent + copy; `onHide` (captured only) soft-deletes the row.
function ReadOnlyEmailPanel({
  emails,
  variant,
  onHide,
  hidingId,
  hideError,
  ownerId,
}: {
  emails: UrgentEmailRow[];
  variant: 'urgent' | 'captured';
  onHide?: (id: string) => void;
  hidingId?: string | null;
  hideError?: string | null;
  ownerId?: string | null;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    nextReadonlySelection(null, emails),
  );
  // Keep the selection valid as the list changes (hide, refetch, tab switch).
  useEffect(() => {
    setSelectedId((curr) => nextReadonlySelection(curr, emails));
  }, [emails]);
  const selected = emails.find((e) => e.id === selectedId) ?? null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
      <div className="lg:col-span-2 space-y-3 lg:max-h-[460px] lg:overflow-y-auto lg:pr-2">
        {emails.map((email) => (
          <ReadOnlyEmailListItem
            key={email.id}
            email={email}
            variant={variant}
            selected={email.id === selectedId}
            onSelect={() => setSelectedId(email.id)}
            onHide={onHide ? () => onHide(email.id) : undefined}
            hiding={hidingId === email.id}
          />
        ))}
        {hideError && <p className="text-[11px] text-alert">{hideError}</p>}
      </div>
      <div className="lg:col-span-3">
        {selected ? (
          <ReadOnlyEmailDetail
            email={selected}
            variant={variant}
            onHide={onHide ? () => onHide(selected.id) : undefined}
            hiding={hidingId === selected.id}
            ownerId={ownerId}
          />
        ) : (
          <GlassCard variant="inset" hover={false}>
            <p className="text-sm text-muted-foreground">Select an email to preview it.</p>
          </GlassCard>
        )}
      </div>
    </div>
  );
}

function ReadOnlyEmailListItem({
  email,
  variant,
  selected,
  onSelect,
  onHide,
  hiding,
}: {
  email: UrgentEmailRow;
  variant: 'urgent' | 'captured';
  selected: boolean;
  onSelect: () => void;
  onHide?: () => void;
  hiding: boolean;
}) {
  const tone = categoryTone(email.category ?? undefined);
  // Select + Hide are SIBLING buttons (not nested) so we never put a <button>
  // inside an interactive card — the whole row-select is the left button.
  return (
    <GlassCard
      hover={false}
      className={`transition-all relative ${
        selected
          ? 'neo-selected before:absolute before:left-1 before:top-3 before:bottom-3 before:w-1 before:rounded-r before:bg-forest'
          : ''
      } ${variant === 'urgent' ? 'neo-alert' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className="min-w-0 flex-1 cursor-pointer text-left"
        >
          <div className="flex items-center gap-2 flex-wrap">
            {variant === 'urgent' && <PulseDot color="red" />}
            <span className="text-sm font-medium text-foreground truncate">
              {email.from_name ?? email.from_email ?? 'unknown sender'}
            </span>
            <span
              className={`neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text} shrink-0`}
            >
              {formatCategory(email.category ?? undefined)}
            </span>
            {email.occurred_at && (
              <span className="ml-auto text-[11px] font-mono text-muted-foreground shrink-0">
                {formatDistanceToNow(new Date(email.occurred_at), { addSuffix: true })}
              </span>
            )}
          </div>
          <p className="text-sm text-foreground truncate mt-0.5">
            {decodeHtmlEntities(email.subject) || '(no subject)'}
          </p>
          {email.snippet && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {decodeHtmlEntities(email.snippet)}
            </p>
          )}
        </button>
        {onHide && (
          <button
            type="button"
            onClick={onHide}
            disabled={hiding}
            aria-label="Hide this email"
            className="neo-chip inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
          >
            <EyeOff className="h-2.5 w-2.5" />
            {hiding ? 'Hiding…' : 'Hide'}
          </button>
        )}
      </div>
    </GlassCard>
  );
}

function ReadOnlyEmailDetail({
  email,
  variant,
  onHide,
  hiding,
  ownerId,
}: {
  email: UrgentEmailRow;
  variant: 'urgent' | 'captured';
  onHide?: () => void;
  hiding: boolean;
  ownerId?: string | null;
}) {
  const tone = categoryTone(email.category ?? undefined);
  // Full plaintext body when we have it, else the snippet — both decoded.
  const body = decodeHtmlEntities(email.body_plain) || decodeHtmlEntities(email.snippet);
  const { data: sentReplies } = useSentReplies(email.id);
  const repliedCount = sentReplyCount(sentReplies);
  const failedCount = failedReplyCount(sentReplies);

  return (
    <GlassCard hover={false} className="flex flex-col lg:sticky lg:top-4">
      <div className="pb-4 border-b border-border">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span
            className={`neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}
          >
            {formatCategory(email.category ?? undefined)}
          </span>
          {variant === 'urgent' && (
            <span className="neo-chip flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-alert/10 text-alert">
              <PulseDot color="red" /> urgent
            </span>
          )}
          {repliedCount > 0 && (
            <span className="neo-chip flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-sage/10 text-sage">
              <CheckCircle2 className="w-3 h-3" /> replied
              {repliedCount > 1 ? ` ×${repliedCount}` : ''}
            </span>
          )}
          {repliedCount === 0 && failedCount > 0 && (
            <span className="neo-chip flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-alert/10 text-alert">
              <PulseDot color="red" /> reply failed
            </span>
          )}
          {email.occurred_at && (
            <span className="ml-auto text-[11px] font-mono text-muted-foreground">
              {formatDistanceToNow(new Date(email.occurred_at), { addSuffix: true })}
            </span>
          )}
        </div>
        <h3 className="text-lg font-medium text-foreground">
          {decodeHtmlEntities(email.subject) || '(no subject)'}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5 truncate">
          From: {email.from_name ?? 'unknown'}
          {email.from_email && ` <${email.from_email}>`}
        </p>
      </div>

      <div className="py-4 flex-1">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Message</p>
        {body ? (
          <pre className="neo-inset p-3 max-h-[52vh] overflow-y-auto whitespace-pre-wrap break-words font-sans text-sm text-foreground leading-relaxed">
            {body}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground">No preview available for this message.</p>
        )}

        {/* The thread of replies we've sent for this email. */}
        <SentRepliesSection sourceEmailId={email.id} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-border">
        <p className="text-[11px] text-muted-foreground/80">
          {repliedCount > 0
            ? `You’ve replied ${repliedCount === 1 ? 'once' : `${repliedCount} times`} — send another below if needed.`
            : failedCount > 0
              ? 'A reply couldn’t be sent — see the thread above and try again.'
              : variant === 'urgent'
                ? 'Flagged urgent — draft a reply below, or hide it.'
                : 'Read + categorized. Draft a reply if you want to respond.'}
        </p>
        <div className="flex items-center gap-2">
          {onHide && (
            <Button variant="outline" size="sm" onClick={onHide} disabled={hiding}>
              <EyeOff className="w-4 h-4 mr-1.5" />
              {hiding ? 'Hiding…' : 'Hide'}
            </Button>
          )}
          {ownerId && email.account_email && (
            <SendReplyButton
              sourceEmailId={email.id}
              accountEmail={email.account_email}
              toEmail={email.from_email}
              toName={email.from_name}
              ownerId={ownerId}
            />
          )}
        </div>
      </div>
    </GlassCard>
  );
}

function DecidedItem({ row }: { row: ApprovalRow }) {
  const label = (() => {
    if (row.status === 'rejected') return 'rejected';
    if (row.status === 'sent') return 'sent';
    if (row.status === 'failed') return 'failed';
    if (row.external_target_id) {
      return row.status === 'edited' ? 'edited · in Gmail Drafts' : 'in Gmail Drafts';
    }
    return `${row.status} · writing…`;
  })();
  const variant: 'default' | 'secondary' | 'destructive' = (() => {
    if (row.status === 'rejected' || row.status === 'failed') return 'destructive';
    if (row.external_target_id) return 'default';
    return 'secondary';
  })();

  // Time shown here is `approvals.created_at` (when the draft was generated)
  // not `emails.occurred_at` (when the email arrived). The pending list uses
  // `occurred_at` because the user is reasoning about the inbound email's
  // recency; the decided list uses `created_at` because the user is
  // reasoning about their own decision's recency.
  return (
    <li className="neo-inset flex items-center justify-between gap-3 px-4 py-2 text-sm">
      <div className="min-w-0 flex-1 truncate">
        <span className="mr-2 text-muted-foreground font-mono text-xs">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
        {decodeHtmlEntities(row.draft_payload.subject ?? row.emails?.subject) || '(no subject)'}
      </div>
      <Badge variant={variant}>{label}</Badge>
    </li>
  );
}

export default Inbox;
